import { useEffect, useMemo, useState } from 'react';
import rules from '../../../../shared/prefillRules.json';
import type { FieldDefinition, FormPrefillSettings, FormWidget, PrefillParam } from '../../shared/types';
import { asksNothing } from '../../shared/types';
import { Modal } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { rotatePrefillKey, signPrefillLink } from '../../api/forms';
import FieldPicker from './FieldPicker';
import { Expand, OptGroup, SecInfo, pickerGroups, subOption, subsOf } from './fieldGroups';

/**
 * Link & access → the link card's "Prefilled link" tab (P1 / prefill
 * playbook A3, A4; since R7 a tab of `LinkPanel` rather than its own card).
 *
 * The owner picks which questions a link may fill; the parameter name in the
 * URL IS the question's id (`email`, `who.firstName`) — nothing to invent and
 * nothing to keep in step. Then the builder turns typed values into a
 * finished link without anyone typing a `?`. Unsigned links fill editable
 * defaults; the signed toggle asks the server for `t` and `sig`, which is
 * what makes a `lock` real — the server re-applies locked values at submit.
 *
 * The rows are validated here exactly as the server's sanitizer will, so a
 * mapping the server would refuse is never sent. Saving rides the Access
 * tab's own save, and the unsaved-changes guard with it.
 */

export interface PrefillTargetOption {
  /** a field key, or `fieldKey.part` for a composite — the parameter name on the link */
  value: string;
  label: string;
  /** hidden in the builder — the link is the only thing that fills it (a hidden field) */
  hidden: boolean;
}

const isHidden = (f: FieldDefinition) => f.ui_meta?.showFlag === '__hidden';
const canFill = (f: FieldDefinition) =>
  f.scope === 'order' && !rules.neverPrefill.includes(f.input_type) && !asksNothing(f.input_type);

/** The questions a link may fill: order scope, an answer rather than an artefact, composites as their parts. */
export function prefillTargets(fields: FieldDefinition[]): PrefillTargetOption[] {
  const out: PrefillTargetOption[] = [];
  for (const f of fields) {
    if (!canFill(f)) continue;
    const hidden = isHidden(f);
    const label = f.label || f.key;
    const parts = subsOf(f).filter((s) => !s.derived);
    if (parts.length) for (const s of parts) out.push({ value: `${f.key}.${s.sub}`, label: `${label} › ${s.label}`, hidden });
    else out.push({ value: f.key, label, hidden });
  }
  return out;
}

/** The same questions arranged the way the form is built — what the row picker draws. */
export function prefillTargetGroups(fields: FieldDefinition[], sections: SecInfo[], widgets: FormWidget[] = []): OptGroup[] {
  const note = (f: FieldDefinition) => (isHidden(f) ? ' (hidden)' : '');
  const expand: Expand = (f) => {
    const parts = subsOf(f).filter((s) => !s.derived);
    if (parts.length) return parts.map((s) => ({ ...subOption(f, s), l: `${subOption(f, s).l}${note(f)}` }));
    return [{ v: f.key, l: `${f.label || f.key} (${f.key})${note(f)}` }];
  };
  return pickerGroups(fields.filter(canFill), sections, widgets, expand, { repeat: false });
}

const PARAM_RE = new RegExp(rules.paramPattern);

/** What the server would refuse, row by row (null = fine), so nothing it would reject is ever sent. */
export function prefillRowProblems(rows: PrefillParam[], targets: PrefillTargetOption[]): (string | null)[] {
  const known = new Set(targets.map((t) => t.value));
  return rows.map((r, i) => {
    const field = (r.field || '').trim();
    const param = (r.param || '').trim() || field;
    if (!field) return null; // an empty row is nothing, and the server drops it
    if (!known.has(field)) return 'That question is not on the published form.';
    if (!PARAM_RE.test(param)) return `"${param}" cannot be a link parameter — letters, digits, dot, dash and underscore only, up to 40 characters.`;
    if (rules.reserved.includes(param)) return `"${param}" is reserved — the form already uses it. Give the question a different id in Build.`;
    if (rows.some((o, j) => j !== i && ((o.param || '').trim() || (o.field || '').trim()) === param)) return `"${param}" is mapped twice.`;
    return null;
  });
}

/** The link, every value encoded, blank ones left out — the base alone when nothing is typed. */
export function buildPrefillUrl(base: string, rows: PrefillParam[], values: Record<string, string>): string {
  const parts: string[] = [];
  for (const r of rows) {
    const param = (r.param || '').trim() || (r.field || '').trim();
    const v = (values[param] || '').trim();
    if (!param || !v) continue;
    parts.push(`${encodeURIComponent(param)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `${base}?${parts.join('&')}` : base;
}

const EXAMPLE = 'https://form-magic.com/f/acme/order?email=dana%40acme.com&plan=pro';

export default function PrefillLinkCard({ formId, fields, sections, widgets = [], value, onChange, publicUrl, saved }: {
  formId: number;
  fields: FieldDefinition[];
  /** where each question lives, so the picker reads like the form */
  sections: SecInfo[];
  widgets?: FormWidget[];
  value: FormPrefillSettings | null;
  onChange: (next: FormPrefillSettings | null) => void;
  /** the absolute public URL of the form, the link builder's base */
  publicUrl: string;
  /** false while the mapping has unsaved edits — only the STORED mapping can be signed */
  saved: boolean;
}) {
  const toast = useToast();
  const rows = value?.params || [];
  const targets = useMemo(() => prefillTargets(fields), [fields]);
  const groups = useMemo(() => prefillTargetGroups(fields, sections, widgets), [fields, sections, widgets]);
  const problems = useMemo(() => prefillRowProblems(rows, targets), [rows, targets]);
  const used = new Set(rows.map((r) => r.field).filter(Boolean));
  const set = (next: PrefillParam[]) => onChange(next.length ? { ...(value || {}), params: next } : null);
  const setRow = (i: number, patch: Partial<PrefillParam>) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  /** The parameter name a row puts on the link: the question's id. */
  const paramOf = (r: PrefillParam) => (r.param || '').trim() || (r.field || '').trim();

  // ---- the builder ----
  const [values, setValues] = useState<Record<string, string>>({});
  const [signed, setSigned] = useState(false);
  const [signedQuery, setSignedQuery] = useState<string | null>(null);
  const [signError, setSignError] = useState('');
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateBusy, setRotateBusy] = useState(false);
  const canSign = saved && rows.length > 0;

  // A signed link signs only what is stored, so the toggle falls off while
  // the mapping has unsaved edits and comes back once they are saved.
  useEffect(() => { if (!canSign) { setSigned(false); setSignedQuery(null); } }, [canSign]);

  useEffect(() => {
    if (!signed || !canSign) return;
    let live = true;
    setSignError('');
    const t = setTimeout(() => {
      signPrefillLink(formId, values)
        .then((r) => { if (live) setSignedQuery(r.query); })
        .catch((e: Error) => { if (live) { setSignedQuery(null); setSignError(e.message); } });
    }, 300);
    return () => { live = false; clearTimeout(t); };
    // `value?.keyVersion` re-signs after a rotation; the rows are stored, so they are covered by `saved`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signed, canSign, values, formId, value?.keyVersion]);

  const url = signed && signedQuery ? `${publicUrl}?${signedQuery}` : buildPrefillUrl(publicUrl, rows, values);
  const usable = rows.filter((r, i) => r.field && !problems[i]);

  function copy() {
    navigator.clipboard?.writeText(url).then(
      () => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); },
      () => toast('Could not copy the link to the clipboard.', { kind: 'error' })
    );
  }

  async function rotate() {
    if (rotateBusy) return;
    setRotateBusy(true);
    try {
      const r = await rotatePrefillKey(formId);
      onChange(r.prefill.params.length ? r.prefill : null);
      setRotating(false);
      toast('Signing key rotated — every signed link issued before now has stopped locking its values.');
    } catch (e: any) {
      toast(e.message || 'Could not rotate the signing key.', { kind: 'error' });
    } finally { setRotateBusy(false); }
  }

  const on = rows.length > 0;
  return (
    <div className="pf-panel">
      <div className="pf-head">
        <span className={`fs-pill ${on ? 'info' : 'neutral'}`}>{on ? 'on' : 'off'}</span>
        <div className="hint">
          Hand someone a link with answers already filled in. Each question you pick here can be filled
          by a parameter named after its id, and anything else on the link is ignored. For example{' '}
          <code className="fs-mono" style={{ fontSize: 12 }}>{EXAMPLE}</code>. A question marked Hidden in
          Fields is still filled this way — that is how a link carries a value nobody is asked for.
        </div>
      </div>
      <div className="pf-body">
        {rows.length === 0 && (
          <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 8 }}>
            Off — the link fills nothing in.
          </div>
        )}
        {rows.map((r, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="pf-row">
              <FieldPicker
                className="fs-input fs-w-300" ariaLabel="Question to fill"
                value={r.field} groups={groups} disabledValues={used}
                onChange={(field) => setRow(i, { field, param: field })}
              />
              <span className="fs-mono" style={{ fontSize: 12.5, color: 'var(--text-muted)' }} title="The parameter name on the link — the question's id">
                {r.field ? `?${paramOf(r)}=…` : ''}
              </span>
              <label className="fs-check" style={{ margin: 0 }} title="Honoured only on a signed link — see the builder below. On an unsigned link the value is an editable default.">
                <input type="checkbox" checked={!!r.lock} onChange={(e) => setRow(i, { lock: e.target.checked || undefined })} />
                <span><b>Lock</b></span>
              </label>
              <button className="fc-x" title="Remove this parameter" onClick={() => set(rows.filter((_, j) => j !== i))}>×</button>
            </div>
            {problems[i] && <div className="ad-err" style={{ marginTop: 4, fontSize: 12.5 }}>{problems[i]}</div>}
          </div>
        ))}
        <button className="fs-btn neutral sm" onClick={() => set([...rows, { param: '', field: '' }])}>+ Add a question</button>
        {rows.some((r) => r.lock) && (
          <div className="hint" style={{ marginTop: 6 }}>
            A lock only holds on a signed link: anyone can type a value into an unsigned one. Turn on
            “Sign this link” below to build one.
          </div>
        )}

        {usable.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-soft)' }}>
            <div className="fb-overline" style={{ marginBottom: 8 }}>Build a link</div>
            <div className="fs-row tight" style={{ flexWrap: 'wrap' }}>
              {usable.map((r) => {
                const param = paramOf(r);
                return (
                  <div className="fs-field pf-build-field" key={param}>
                    <label htmlFor={`fa-pf-${param}`} className="fs-mono">
                      {param}{r.lock && signed ? <span className="fs-pill info" style={{ marginLeft: 6 }}>locked</span> : null}
                    </label>
                    <input
                      id={`fa-pf-${param}`} value={values[param] || ''} placeholder="leave blank to omit"
                      onChange={(e) => setValues({ ...values, [param]: e.target.value })}
                    />
                  </div>
                );
              })}
            </div>
            <label className="fs-check" style={canSign ? undefined : { opacity: .6 }}>
              <input type="checkbox" checked={signed} disabled={!canSign} onChange={(e) => setSigned(e.target.checked)} />
              <span>
                <b>Sign this link (lock these values)</b>
                <small>
                  {!saved
                    ? 'Save the mapping first — a signed link vouches for what is stored.'
                    : `The link gains a signature the form checks before locking anything. Good for ${rules.signedLinkDays} days; “Rotate signing key” cancels every one issued so far.`}
                </small>
              </span>
            </label>
            {signError && <div className="ad-err" style={{ marginTop: 6 }}>{signError}</div>}
            <div className="fs-field" style={{ marginTop: 8 }}>
              <label htmlFor="fa-pf-url">Your link</label>
              <input id="fa-pf-url" className="fs-mono" readOnly value={signed && !signedQuery ? 'Signing…' : url} onFocus={(e) => e.currentTarget.select()} />
            </div>
            <div className="pf-actions">
              <button className="fs-btn neutral sm" onClick={copy} disabled={signed && !signedQuery}>{copied ? 'Copied' : 'Copy link'}</button>
              <a className="fs-btn neutral sm" href={url} target="_blank" rel="noopener noreferrer">Test it ↗</a>
              {saved && (
                <button className="fs-btn neutral sm pf-rotate" onClick={() => setRotating(true)}>Rotate signing key…</button>
              )}
            </div>
          </div>
        )}
      </div>

      {rotating && (
        <Modal
          title="Rotate the signing key?" narrow onClose={() => setRotating(false)}
          footer={<>
            <button className="fs-btn danger" onClick={rotate} disabled={rotateBusy}>{rotateBusy ? 'Rotating…' : 'Rotate the key'}</button>
            <button className="fs-btn neutral" onClick={() => setRotating(false)}>Cancel</button>
          </>}
        >
          <div className="fs-consequence">
            <b>Every previously issued signed link stops verifying.</b>{' '}
            Those links still open the form and still fill their values in, but nothing on them is
            locked any more. Links you build after this are signed with the new key.
          </div>
        </Modal>
      )}
    </div>
  );
}
